import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Helmet from 'react-helmet/es/Helmet';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Popover from '@material-ui/core/Popover';
import ErrorIcon from '@material-ui/icons/Error';
import Pagination from '@material-ui/lab/Pagination';
import MaterialTable from 'material-table';
import FileSaver from 'file-saver';
import * as jsonexport from 'jsonexport/dist';
import PopupState, { bindTrigger, bindPopover } from 'material-ui-popup-state';
import Papa from 'papaparse';
import { useMutation, useQuery, useApolloClient } from 'react-apollo';
import moment from 'moment';
import omit from 'lodash/omit';
import {
  INSERT_COLLEGES,
  GET_COLLEGES,
  INSERT_DEADLINES,
  UPDATE_DEADLINE,
  DELETE_DEADLINE,
  GET_COLLEGE_DEADLINES,
  DELETE_COLLEGE,
  GET_ADMISSION_TYPES
} from 'graphql/colleges';
import useSnack from 'hooks/useSnack';
import SearchBar from 'components/common/Search';
import Spin from 'components/common/Spin';
import Logs from '../components/Logs';
import AddCollege from '../components/AddCollege';

const useStyles = makeStyles((theme) => ({
  root: {
    '& > *': {
      margin: theme.spacing(1),
    },
  },
  input: {
    display: 'none',
  },
}));

const CollegeList = ({}) => {
  const classes = useStyles();
  const snack = useSnack();
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState();
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [selectedRow, setSelectedRow] = useState();
  const client = useApolloClient();
  const [insertColleges] = useMutation(INSERT_COLLEGES);
  const [deleteCollege] = useMutation(DELETE_COLLEGE);
  const [insertDeadlines] = useMutation(INSERT_DEADLINES);
  const [deleteDeadline] = useMutation(DELETE_DEADLINE);
  const [updateDeadline] = useMutation(UPDATE_DEADLINE);
  const collegesResponse = useQuery(GET_COLLEGES, {
    variables: {
      limit: 10,
      offset: 0,
      query: null,
    },
  });
  const admissionTypeResponse = useQuery(GET_ADMISSION_TYPES);

  const onCollegeFileChange = (e) => {
    Papa.parse(e.target.files[0], {
      header: true,
      complete: function(results) {
        const data = results.data.map((i) => ({
          id: i.UNITID,
          name: i.INSTNM,
          website_url: i.INSTURL,
          deadline_url: i['Deadline URL'],
        }));
        snack.info('Inserting records...');
        insertColleges({
          variables: {
            data,
          },
        }).then(
          () => {
            snack.success('Colleges imported');
            collegesResponse.refetch();
          },
          (e) => snack.error(e.message),
        );
      },
    });
  };

  const onDeadlineFileChange = (e) => {
    snack.info('Reading File...');
    Papa.parse(e.target.files[0], {
      header: true,
      complete: function(results) {
        const data = results.data.map((i) => ({
          id: i.id,
          college_id: i.UNITID,
          program_type: i['Type or Special Program (If Applicable)'],
          admission_type: i['Admission Type'],
          deadline: i['Application Deadline'] && moment(i['Application Deadline'], 'MM/DD/YYYY').toJSON(),
        }));

        const filtered = data.filter(({ id, ...other }) => {
          return !Object.values(other).some((v) => !v)
        });

        snack.info(`Inserting ${filtered.length} records...`);

        insertDeadlines({
          variables: {
            data: filtered,
          },
        }).then(
          () => {
            snack.success('Deadlines imported');
            collegesResponse.refetch();
          },
          (e) => snack.error(e.message),
        );
      },
    });
  };

  const onQueryChange = (value) => {
    setQuery(value);
  };
  const onSearch = () => {
    collegesResponse.refetch({
      query: `%${query}%`,
      offset: 0,
    });
  };

  const handleSkipSuggestion = (row, popupState) => () => {
    popupState.close();
    updateDeadline({
      variables: {
        id: row.id,
        data: {
          flagged: false,
          flagged_by: null,
          suggested_date: null,
        },
      },
    })
      .then(() => snack.success('Suggestion skipped'))
      .catch((e) => snack.error(e.message));
  };

  const handleConfirmSuggestion = (row, popupState) => () => {
    popupState.close();
    updateDeadline({
      variables: {
        id: row.id,
        data: {
          flagged: false,
          flagged_by: null,
          suggested_date: null,
          deadline: row.suggested_date,
        },
      },
    })
      .then(() => snack.success('Suggestion accepted'))
      .catch((e) => snack.error(e.message));
  };

  const onPageChange = (e, val) => {
    setPage(val);
    collegesResponse.refetch({
      offset: (val - 1) * 10,
      query: `%${query}%`,
    });
  };

  const onDownload = () => {
    snack.info('Downloading...');
    client
      .query({
        query: GET_COLLEGE_DEADLINES,
      })
      .then((resp) => {
        jsonexport(
          resp.data.rows.map((i) =>
            omit({ ...i, deadline: moment(i.deadline).format('MM/DD/YYYY') }, [
              'college.__typename',
              '__typename',
              'flagged_by'
            ]),
          ),
          {
            headers: [
              'id',
              'college.id',
              'college.name',
              'program_type',
              'admission_type',
              'deadline',
            ],
            rename: [
              'id',
              'UNITID',
              'INSTNM',
              'Type or Special Program (If Applicable)',
              'Admission Type',
              'Application Deadline',
            ],
          },
          (err, csv) => {
            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
            FileSaver.saveAs(blob, 'deadline_export.csv');
            snack.success('File downloaded');
          },
        );
      });
  };

  const toggleAdd = () => setShowAdd(!showAdd);

  const upsertCollege = (value) => {
    return insertColleges({
      variables: {
        data: [value]
      }
    }).then(
      () => {
        collegesResponse.refetch();
      }
    );
  }

  const onDeleteCollege = (id) => {
    if (window.confirm('Delete this college and associated data?')) {
      return deleteCollege({
        variables: { id }
      }).then(
        () => {
          collegesResponse.refetch();
        }
      )
    }
  }

  const onDeleteDeadline = (row) => {
    return deleteDeadline({
      variables: {id: row.id }
    }).then(
      () => {
        collegesResponse.refetch();
      }
    );
  };

  const onDeadlineAdd = async (data, college) => {
    await insertDeadlines({
      variables: {
        data: [{...data, college_id: college.id}]
      }
    });
    collegesResponse.refetch();
  }

  const onUpdateDeadline = async (newVal, oldVal) => {
    await updateDeadline({
      variables: {
        id: newVal.id,
        data: {
          deadline: newVal.deadline,
          program_type: newVal.program_type,
          admission_type: newVal.admission_type
        }
      }
    });
    collegesResponse.refetch();
  };

  const admissionTypeLookup = admissionTypeResponse.data?.college_application_deadlines_aggregate.nodes.reduce((memo, row) => {
    memo[row.admission_type] = row.admission_type;
    return memo;
  }, {});

  const columns = [
    { title: 'ID', field: 'id', editable: 'never' },
    { title: 'Type or Special Program', field: 'program_type' },
    { title: 'Admission Type', field: 'admission_type', lookup: admissionTypeLookup, },
    {
      title: 'Application Deadline',
      field: 'deadline',
      type: 'date',
      render: (row) => {
        if (row.flagged) {
          return (
            <PopupState variant="popover" popupId={`deadline-${row.id}`}>
              {(popupState) => (
                <div>
                  <Box display="inline-block" {...bindTrigger(popupState)}>
                    <Box display="flex" alignItems="center">
                      <ErrorIcon color="error" />
                      &nbsp;
                      <Typography
                        variant="body2"
                        color="error"
                        style={{ cursor: 'pointer' }}
                      >
                        {moment(row.deadline).format('L')}
                      </Typography>
                    </Box>
                  </Box>
                  <Popover
                    {...bindPopover(popupState)}
                    anchorOrigin={{
                      vertical: 'top',
                      horizontal: 'center',
                    }}
                    transformOrigin={{
                      vertical: 'bottom',
                      horizontal: 'center',
                    }}
                  >
                    <Box padding={2}>
                      <Typography variant="body2">
                        Correct date:{' '}
                        <strong>
                          {moment(row.suggested_date).format('L')}
                        </strong>
                      </Typography>
                      <Typography variant="body2">
                        Suggested by{' '}
                        <strong>
                          {row.flagged_user.user_first_name}{' '}
                          {row.flagged_user.user_last_name}
                        </strong>
                      </Typography>
                      <Box marginTop={2} textAlign="right">
                        <Button
                          variant="text"
                          color="secondary"
                          onClick={handleSkipSuggestion(row, popupState)}
                        >
                          Skip
                        </Button>{' '}
                        <Button
                          variant="text"
                          color="primary"
                          onClick={handleConfirmSuggestion(row, popupState)}
                        >
                          Confirm
                        </Button>
                      </Box>
                    </Box>
                  </Popover>
                </div>
              )}
            </PopupState>
          );
        }
        return moment(row.deadline).format('L');
      },
    },
  ];

  return (
    <Box>
      <Helmet title="College Database" />
      <Box marginBottom={2}>
        <Button variant="contained" color="primary" onClick={toggleAdd}>Add College</Button>
        {' '}
        <input
          accept="text/csv"
          className={classes.input}
          id="college-button-file"
          type="file"
          onChange={onCollegeFileChange}
        />
        <label htmlFor="college-button-file">
          <Button variant="contained" color="primary" component="span">
            Import Colleges
          </Button>
        </label>{' '}
        <input
          accept="text/csv"
          className={classes.input}
          id="deadline-button-file"
          type="file"
          onChange={onDeadlineFileChange}
        />
        <label htmlFor="deadline-button-file">
          <Button variant="contained" color="primary" component="span">
            Import Deadlines
          </Button>
        </label>{' '}
        <Button variant="contained" color="primary" onClick={onDownload}>
          Download CSV
        </Button>
      </Box>
      <Box marginBottom={2}>
        <SearchBar
          varient="outlined"
          inputVal={query}
          onChange={onQueryChange}
          onSearch={onSearch}
          placeholder="Enter college name"
        />
      </Box>
      {collegesResponse.loading ? (
        <Spin spinning />
      ) : (
        <>
          {collegesResponse.data.colleges.length ? (
            <>
              {collegesResponse.data.colleges.map((c) => (
                <Box key={c.id} marginBottom={3}>
                  <MaterialTable
                    title={c.name}
                    options={{
                      search: false,
                      paging: false,
                      actionsColumnIndex: -1,
                    }}
                    editable={{
                      onRowAdd: (val) => onDeadlineAdd(val, c),
                      onRowUpdate: onUpdateDeadline,
                      onRowDelete: onDeleteDeadline
                    }}
                    columns={columns}
                    data={c.deadlines}
                    actions={[
                      {
                        icon: 'history',
                        tooltip: 'View logs',
                        onClick: (event, rowData) => {
                          setSelectedRow(rowData);
                        },
                      },
                      {
                        icon: 'edit',
                        tooltip: 'Edit College',
                        isFreeAction: true,
                        onClick: () => setEditing(c)
                      },
                      {
                        icon: 'delete',
                        tooltip: 'Delete College',
                        isFreeAction: true,
                        onClick: () => onDeleteCollege(c.id)
                      }
                    ]}
                  />
                </Box>
              ))}
              <Box display="flex" justifyContent="center" marginTop={5}>
                <Pagination
                  count={Math.floor(
                    collegesResponse.data.colleges_aggregate.aggregate.count /
                      10,
                  )}
                  color="primary"
                  page={page}
                  onChange={onPageChange}
                />
              </Box>
            </>
          ) : (
            'No colleges found'
          )}
        </>
      )}
      {selectedRow && (
        <Logs id={selectedRow.id} onClose={() => setSelectedRow()} />
      )}
      {showAdd && (
        <AddCollege onClose={toggleAdd} onSave={upsertCollege} />
      )}
      {editing && (
        <AddCollege college={editing} onClose={() => setEditing()} onSave={upsertCollege} />
      )}
    </Box>
  );
};

export default CollegeList;
